// ---------------------------------------------------------------------------
// The campaign page's requests, and what it does with each answer (R16a)
//
// Known bugs of the campaign page (pages/campaign-detail.ts), fixed in today's
// code before the page is rebuilt (docs/design/REDESIGN-PLAN.md, R16a):
//
//   - "Not found" for every failure. Every answer of GET /api/campaigns/:id
//     that was not { ok: true } (a 500, an expired session), a network error
//     and a body that was not the campaign all left the campaign unset, and
//     the page said "We couldn't find that campaign. It may have been
//     deleted." Only the server's own 404 means that. Anything else is "could
//     not be loaded", with the reason and Try again.
//   - The reply drafts' actions said nothing when they failed. Approve & Send,
//     Send with Email / LinkedIn, Edit, Regenerate and Reject swallowed every
//     refusal. An approval the server refused (Do Not Contact, opted out, a
//     draft no longer pending) looked as if nothing had happened, and the
//     draft the server had just marked "Send failed" still read "Pending".
//   - Reject had no lock. A second click sent a second request, and Reject
//     stayed live while an approval of the same draft was on its way.
//   - The reply prompt was taken over when the server refused it: the page
//     showed, as saved, a prompt the server never stored.
//
// Plain TypeScript, no Adornis and no Lit, so all of it is tested by behaviour
// with a stand-in for fetch (tests/campaign-detail-prefixes.test.ts); the page
// hands in its own setters and its toast. Every failure is also written to the
// browser console under one prefix, "[campaign-detail]", so a recurrence can
// be seen there: the client has no telemetry of its own.
// ---------------------------------------------------------------------------

import { NO_CONNECTION } from './dashboard-load.js';

export { NO_CONNECTION };

/** fetch, or a stand-in for it. */
export type Send = (url: string, init?: RequestInit) => Promise<Response>;

/** Where a failure is written so that it can be seen again. */
export type Log = (event: string, detail: Record<string, unknown>) => void;

/** The prefix of every line this page writes to the console. */
export const LOG_PREFIX = '[campaign-detail]';

/** The page's words when a load got no readable answer, as before R16a (error line and toast). */
export const LOAD_FAILED = 'Failed to load campaign data';

const viaFetch: Send = (url, init) => fetch(url, init);
const toConsole: Log = (event, detail) => console.warn(`${LOG_PREFIX} ${event}`, detail);

/**
 * What one request came to. `status` is null when no answer came at all, and
 * `answered` says whether the body was the API's own JSON (a proxy's error page
 * is not).
 */
export type Outcome =
  | { ok: true; status: number; data: Record<string, unknown> }
  | { ok: false; status: number | null; answered: boolean; error: string };

/**
 * Sends one request and never throws. Only an HTTP success whose body says
 * `ok: true` is a success; every other answer is a failure with a reason: the
 * server's own words, else the status, else that the answer could not be read.
 */
export async function ask(url: string, init?: RequestInit, send: Send = viaFetch): Promise<Outcome> {
  let resp: Response;
  try {
    resp = await send(url, init);
  } catch {
    return { ok: false, status: null, answered: false, error: NO_CONNECTION };
  }
  let parsed: unknown = null;
  try {
    parsed = await resp.json();
  } catch {
    parsed = null;
  }
  const body =
    parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  if (resp.ok && body?.ok === true) return { ok: true, status: resp.status, data: body };
  const reason = typeof body?.error === 'string' ? body.error.trim() : '';
  const error =
    reason ||
    (!resp.ok
      ? `The server answered ${resp.status}.`
      : body
        ? 'The server did not confirm it.'
        : "The server's answer could not be read.");
  return { ok: false, status: resp.status, answered: body !== null, error };
}

// ── The campaign ─────────────────────────────────────────────────────────────

/** Why there is no campaign to show. `notFound` only for the server's own 404. */
export interface CampaignLoadFailure {
  notFound: boolean;
  error: string;
}

/**
 * The page's own setters. Declared as methods, so the page's typed setters fit
 * as they are.
 */
export interface CampaignPageSetters<C> {
  setCampaign(campaign: C): void;
  setReplyPromptText(text: string): void;
  setLoadFailure(failure: CampaignLoadFailure | null): void;
  setLeads(leads: unknown[]): void;
  setMessages(messages: unknown[]): void;
  setLinkedinHealth(health: unknown): void;
  setLinkedinJobs(jobs: unknown[]): void;
  setAccountStates(budget: unknown[]): void;
  setAccountNames(names: Record<string, string>): void;
  setSendingStatus(status: { window: unknown; limits: unknown[] } | null): void;
  /** The page's error line. */
  setError(message: string): void;
  /** The page's error toast. */
  fail(message: string): void;
}

function readCampaign<C>(outcome: Outcome): { ok: true; campaign: C } | { ok: false; failure: CampaignLoadFailure } {
  if (!outcome.ok) {
    return { ok: false, failure: { notFound: outcome.status === 404 && outcome.answered, error: outcome.error } };
  }
  const campaign = outcome.data.campaign;
  if (
    campaign !== null &&
    typeof campaign === 'object' &&
    !Array.isArray(campaign) &&
    typeof (campaign as { _id?: unknown })._id === 'string'
  ) {
    return { ok: true, campaign: campaign as C };
  }
  return { ok: false, failure: { notFound: false, error: "The server's answer held no campaign." } };
}

const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/**
 * Loads the campaign, its leads, messages, LinkedIn jobs and sending status at
 * once, and hands each answer to the page. Never throws.
 *
 * The campaign decides what the page can show: when it loaded, the page shows
 * it and the failure is cleared; when it did not, the page keeps whatever it
 * already shows (a refresh that failed) and records why, so a first load can
 * say "not found" only for the server's 404 and "could not be loaded" for
 * everything else. When any of the five requests got no readable answer, the
 * page's error line and toast say "Failed to load campaign data", as they did.
 *
 * Each of the other four keeps its list as it was when it did not load, as an
 * answer ok:false always did; one list failing on its own no longer throws the
 * whole refresh away. Each one that failed is written to the console.
 */
export async function loadCampaignPage<C extends { replyPrompt?: string }>(
  campaignId: string,
  set: CampaignPageSetters<C>,
  { send = viaFetch, log = toConsole }: { send?: Send; log?: Log } = {},
): Promise<void> {
  const base = `/api/campaigns/${encodeURIComponent(campaignId)}`;
  const [campaign, leads, messages, jobs, sending] = await Promise.all([
    ask(base, undefined, send),
    ask(`${base}/leads`, undefined, send),
    ask(`${base}/messages`, undefined, send),
    ask(`${base}/linkedin-jobs`, undefined, send),
    ask(`${base}/sending-status`, undefined, send),
  ]);

  const read = readCampaign<C>(campaign);
  if (read.ok) {
    set.setCampaign(read.campaign);
    set.setReplyPromptText(read.campaign.replyPrompt ?? '');
    set.setLoadFailure(null);
  } else {
    set.setLoadFailure(read.failure);
    log(read.failure.notFound ? 'campaign not found' : 'campaign could not be loaded', {
      campaignId,
      status: campaign.status,
      error: read.failure.error,
    });
  }

  // A list that did not load is written to the console, so a page that shows a
  // campaign without its leads can be told apart from one that has none (R16a
  // review: the first fix left no trace of it). Not when the campaign itself
  // failed: its line already says why, and the lists fail with it.
  if (read.ok) {
    for (const [what, outcome] of [
      ['leads', leads],
      ['messages', messages],
      ['linkedin jobs', jobs],
      ['sending status', sending],
    ] as const) {
      if (!outcome.ok)
        log(`campaign ${what} could not be loaded`, { campaignId, status: outcome.status, error: outcome.error });
    }
  }
  // As before R16a, a request that got no readable answer (no connection, an
  // answer that is not JSON) puts "Failed to load campaign data" in the error
  // line and a toast, once per load. A refusal the server explained only goes to
  // the console, so a poll does not toast every few seconds.
  if ([campaign, leads, messages, jobs, sending].some(o => !o.ok && !o.answered)) {
    set.setError(LOAD_FAILED);
    set.fail(LOAD_FAILED);
  }

  if (leads.ok) set.setLeads(list(leads.data.leads));
  if (messages.ok) set.setMessages(list(messages.data.messages));
  if (jobs.ok) {
    const j = jobs.data;
    if (j.health) set.setLinkedinHealth(j.health);
    if (Array.isArray(j.jobs)) set.setLinkedinJobs(j.jobs);
    if (Array.isArray(j.budget)) set.setAccountStates(j.budget);
    if (j.accountNames) set.setAccountNames(j.accountNames as Record<string, string>);
  }
  if (sending.ok && sending.data.window) {
    set.setSendingStatus({ window: sending.data.window, limits: list(sending.data.limits) });
  }
}

/**
 * What stands where the campaign would be, when there is none: null when the
 * server said it does not exist (the page's own "not found"), otherwise what
 * could not be loaded and why.
 */
export function campaignUnavailable(failure: CampaignLoadFailure | null): { title: string; reason: string } | null {
  if (!failure || failure.notFound) return null;
  return { title: 'This campaign could not be loaded', reason: failure.error };
}

// ── Reply drafts ─────────────────────────────────────────────────────────────

export type DraftChannel = 'email' | 'linkedin_dm';

export interface DraftActionDeps {
  /**
   * The drafts with a request on its way. One Set for the page's lifetime: a
   * second click is ignored at once, before the page has re-rendered.
   */
  inFlight: Set<string>;
  /** Marks the draft whose buttons are disabled, or none. */
  setBusy(draftId: string | null): void;
  /** Reloads the page's drafts. */
  reload(): Promise<void>;
  /** The page's error toast. */
  fail(message: string): void;
  send?: Send;
  log?: Log;
}

interface DraftRequest {
  /** What failed, as the toast says it. */
  what: string;
  event: string;
  url: string;
  init: RequestInit;
}

/**
 * The four actions of a reply draft. Each holds a lock on its draft while its
 * request runs (one action per draft at a time: Reject cannot overtake an
 * approval), says what failed and why, reloads the drafts after every answer
 * (a refused approval is "Send failed" on the server), and always releases the
 * draft. Each resolves to whether the server did it, and never throws.
 */
export function draftActions(deps: DraftActionDeps) {
  const send = deps.send ?? viaFetch;
  const log = deps.log ?? toConsole;

  const run = async (draftId: string, request: DraftRequest): Promise<boolean> => {
    if (deps.inFlight.has(draftId)) return false;
    deps.inFlight.add(draftId);
    deps.setBusy(draftId);
    try {
      const outcome = await ask(request.url, request.init, send);
      if (!outcome.ok) {
        deps.fail(`${request.what}: ${outcome.error}`);
        log(`draft ${request.event} failed`, { draftId, status: outcome.status, error: outcome.error });
      }
      try {
        await deps.reload();
      } catch {
        // The list stays as it was; the toast above already said what failed.
      }
      return outcome.ok;
    } finally {
      deps.inFlight.delete(draftId);
      // Another draft's request may still be on its way: keep its buttons disabled.
      deps.setBusy([...deps.inFlight].pop() ?? null);
    }
  };

  const path = (draftId: string, action: string) => `/api/drafts/${encodeURIComponent(draftId)}/${action}`;
  const JSON_BODY = { 'Content-Type': 'application/json' };

  return {
    approve: (draftId: string, channel?: DraftChannel) =>
      run(draftId, {
        what: 'Could not approve the draft',
        event: 'approve',
        url: path(draftId, 'approve'),
        init: {
          method: 'PUT',
          headers: channel ? JSON_BODY : undefined,
          body: channel ? JSON.stringify({ channel }) : undefined,
        },
      }),
    reject: (draftId: string) =>
      run(draftId, {
        what: 'Could not reject the draft',
        event: 'reject',
        url: path(draftId, 'reject'),
        init: { method: 'PUT' },
      }),
    edit: (draftId: string, message: string) =>
      run(draftId, {
        what: 'Could not save the edited draft',
        event: 'edit',
        url: path(draftId, 'edit'),
        init: { method: 'PUT', headers: JSON_BODY, body: JSON.stringify({ message }) },
      }),
    regenerate: (draftId: string) =>
      run(draftId, {
        what: 'Could not regenerate the draft',
        event: 'regenerate',
        url: path(draftId, 'regenerate'),
        init: { method: 'POST' },
      }),
  };
}

// ── The reply prompt ─────────────────────────────────────────────────────────

export interface ReplyPromptDeps {
  campaignId: string;
  prompt: string;
  setSaving(saving: boolean): void;
  /** Only once the server stored it: the page takes the prompt over and closes the editor. */
  saved(prompt: string): void;
  /** The page's error toast. */
  fail(message: string): void;
  send?: Send;
  log?: Log;
}

/**
 * Saves the campaign's reply prompt. The page takes the prompt over only when
 * the server stored it; on a refusal the editor stays open with the text, and
 * the toast says why. Resolves to whether it was saved, never throws, and
 * always releases Save.
 */
export async function saveCampaignReplyPrompt(deps: ReplyPromptDeps): Promise<boolean> {
  deps.setSaving(true);
  try {
    // The reply model is chosen on the server; the page sends only the prompt.
    const outcome = await ask(
      `/api/campaigns/${encodeURIComponent(deps.campaignId)}/reply-prompt`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ replyPrompt: deps.prompt }),
      },
      deps.send ?? viaFetch,
    );
    if (!outcome.ok) {
      deps.fail(`Could not save the reply prompt: ${outcome.error}`);
      (deps.log ?? toConsole)('reply prompt not saved', {
        campaignId: deps.campaignId,
        status: outcome.status,
        error: outcome.error,
      });
      return false;
    }
    deps.saved(deps.prompt);
    return true;
  } finally {
    deps.setSaving(false);
  }
}
